import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const INACTIVITY_TIMEOUT = 600000; // 10 minutes in ms
const WARNING_TIMEOUT = 540000; // 9 minutes in ms

interface UseInactivityLogoutReturn {
  showWarning: boolean;
  remainingTime: number;
  stayLoggedIn: () => void;
}

export function useInactivityLogout(): UseInactivityLogoutReturn {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(60);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check if user is admin
  const checkAdminStatus = useCallback(async (userId: string) => {
    try {
      const { data: hasAdminRole, error } = await supabase
        .rpc('has_role', { _user_id: userId, _role: 'admin' });
      
      if (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        return;
      }
      
      setIsAdmin(hasAdminRole || false);
    } catch (error) {
      console.error('Error in admin check:', error);
      setIsAdmin(false);
    }
  }, []);

  // Logout function
  const performLogout = useCallback(async () => {
    clearAllTimers();
    setShowWarning(false);
    
    await supabase.auth.signOut();
    
    toast({
      title: "Automatisch uitgelogd",
      description: "Je bent automatisch uitgelogd wegens inactiviteit.",
      variant: "default",
    });
    
    navigate('/auth');
  }, [navigate, toast]);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // Start countdown when warning is shown
  const startCountdown = useCallback(() => {
    setRemainingTime(60);
    
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    
    countdownRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Reset activity timer
  const resetTimer = useCallback(() => {
    if (isAdmin === true || !isAuthenticated) return;
    
    lastActivityRef.current = Date.now();
    clearAllTimers();
    setShowWarning(false);
    setRemainingTime(60);

    // Set warning timer (9 minutes)
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      startCountdown();
    }, WARNING_TIMEOUT);

    // Set logout timer (10 minutes)
    inactivityTimerRef.current = setTimeout(() => {
      performLogout();
    }, INACTIVITY_TIMEOUT);
  }, [isAdmin, isAuthenticated, clearAllTimers, startCountdown, performLogout]);

  // Stay logged in handler
  const stayLoggedIn = useCallback(() => {
    setShowWarning(false);
    resetTimer();
  }, [resetTimer]);

  // Setup auth listener
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setIsAuthenticated(true);
        checkAdminStatus(session.user.id);
      } else {
        setIsAuthenticated(false);
        setIsAdmin(null);
        clearAllTimers();
        setShowWarning(false);
      }
    });

    // Check existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setIsAuthenticated(true);
        checkAdminStatus(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkAdminStatus, clearAllTimers]);

  // Setup activity listeners when authenticated and not admin
  useEffect(() => {
    if (!isAuthenticated || isAdmin === null) return;
    if (isAdmin === true) {
      clearAllTimers();
      return;
    }

    // Start initial timer
    resetTimer();

    // Activity events
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    
    const handleActivity = () => {
      // Throttle activity detection to avoid excessive timer resets
      const now = Date.now();
      if (now - lastActivityRef.current > 1000) {
        resetTimer();
      }
    };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Intercept fetch to detect API activity
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      handleActivity();
      return originalFetch(...args);
    };

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      window.fetch = originalFetch;
      clearAllTimers();
    };
  }, [isAuthenticated, isAdmin, resetTimer, clearAllTimers]);

  return {
    showWarning,
    remainingTime,
    stayLoggedIn,
  };
}
